import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import Header from "components/common/Header/Header";
import SideBar from "components/common/SideBar/SideBarNew";
import { LoginContext } from "helpers/LoginContext";
import { classNames } from "Utils/utils";
import { useBodyScrollLock } from "pages/Rostering/UpdatedTimeSheet/TimeSheetDetailsSection/ai/utils/bodyScrollLock";
import {
  TimesheetAiConfirmModal,
  TimesheetAiProgressModal,
} from "pages/Rostering/UpdatedTimeSheet/TimeSheetDetailsSection/ai";
import { useTimesheetAiAnalyseFlow } from "pages/Rostering/UpdatedTimeSheet/TimeSheetDetailsSection/ai/hooks/useTimesheetAiAnalyseFlow";
import careWatchApi from "store/services/careWatch.services";
import { toApiFilters } from "./utils/signalAdapter";
import { kpiClickFilterUpdate, selectedCategories } from "./utils/queueUtils";
import {
  CARE_WATCH_VIEWS,
  CATEGORY_OPTIONS,
  isCareWatchLiveView,
} from "./utils/constants";
import { careWatchSignalToTimesheetRow } from "./utils/careWatchTimesheetRow";
import CareWatchKpiStrip from "./components/CareWatchKpiStrip";
import CareWatchFilterBar from "./components/CareWatchFilterBar";
import CareWatchPageTitle from "./components/CareWatchPageTitle";
import CareWatchPageSubtitle from "./components/CareWatchPageSubtitle";
import CareWatchWorkArea from "./components/CareWatchWorkArea";
import CareWatchQueueTabs from "./components/CareWatchQueueTabs";
import CareWatchNoticeBanner from "./components/CareWatchNoticeBanner";
import {
  CareWatchFilterSkeleton,
  CareWatchKpiSkeleton,
} from "./components/CareWatchSkeletons";

const SHEET_DURATION_MS = 320;
const OVERLAY_MAX_WIDTH_MQ = "(max-width: 1279px)";

const isOverlayViewport = () =>
  typeof window !== "undefined" &&
  window.matchMedia(OVERLAY_MAX_WIDTH_MQ).matches;

const CareWatchPage = (props) => {
  const {
    filters,
    signalDates,
    kpis,
    overview,
    hasActiveFilters,
    matchCaption,
    pageItems,
    total,
    page,
    pageSize,
    totalPages,
    selected,
    promoteHighlight,
    listLoading,
    acknowledgeLoading,
    promoteLoading,
    detailLoading,
    loadQueue,
    loadDetail,
    updateFilter,
    updateFilters,
    clearFilters,
    setPage,
    setPageSize,
    toggleRow,
    setSelectedId,
    toggleChecked,
    acknowledgeIds,
    promoteItem,
    changeRisk,
    riskSavingId,
    resetCareWatch,
    loadOverview,
    careWatchUpdatedPusher,
    resetCareWatchUpdatedPusher,
  } = props;

  const { sideBarPadding } = useContext(LoginContext);

  useEffect(() => {
    return () => {
      resetCareWatch();
    };
  }, [resetCareWatch]);

  // Filtering is done server-side. Overview omits the tile buckets (risk,
  // issue, live_state) so the KPIs stay a clickable breakdown rather than
  // collapsing to the one bucket already selected — and a tile click does
  // not refetch overview.
  const isLiveView = isCareWatchLiveView(filters.view);
  const listFilters = toApiFilters(filters);
  const overviewFilters = toApiFilters(filters, {
    omit: ["risk", "issue", "live_state"],
  });

  // Tag live requests so the saga calls the correct endpoint.
  // Signal pages are cut on the server. Live stays a single floor list.
  const taggedListFilters = isLiveView
    ? { ...listFilters, _live: true }
    : { ...listFilters, page, per_page: pageSize };
  const taggedOverviewFilters = isLiveView
    ? { ...overviewFilters, _live: true }
    : overviewFilters;

  const listFiltersKey = JSON.stringify(taggedListFilters);
  const tableQueryKey = JSON.stringify(
    isLiveView ? { ...listFilters, _live: true } : listFilters
  );
  const overviewFiltersKey = JSON.stringify(taggedOverviewFilters);
  const listFiltersRef = useRef(taggedListFilters);
  const overviewFiltersRef = useRef(taggedOverviewFilters);
  const selectedRef = useRef(selected);
  listFiltersRef.current = taggedListFilters;
  overviewFiltersRef.current = taggedOverviewFilters;
  selectedRef.current = selected;

  const refreshQueue = useCallback(() => {
    if (isLiveView) return;
    loadQueue(listFiltersRef.current);
    loadOverview(overviewFiltersRef.current);
    loadTabCountsRef.current?.();
    const open = selectedRef.current;
    if (open?.timesheetUuid) {
      loadDetail({
        uuid: open.timesheetUuid,
        incidentKey: open.incidentKey,
      });
    }
  }, [loadQueue, loadOverview, loadDetail, isLiveView]);

  const wasAcknowledgingRef = useRef(false);
  useEffect(() => {
    if (acknowledgeLoading) {
      wasAcknowledgingRef.current = true;
      return;
    }
    if (!wasAcknowledgingRef.current) return;
    wasAcknowledgingRef.current = false;
    refreshQueue();
  }, [acknowledgeLoading, refreshQueue]);

  const {
    streaming: reanalyseStreaming,
    startAnalyse: startReanalyse,
    confirmModalProps,
    progressModalProps,
  } = useTimesheetAiAnalyseFlow({
    onComplete: refreshQueue,
    mapItemToRow: careWatchSignalToTimesheetRow,
    releaseScrollOnSwap: false,
    successMessage: "AI anomaly analysis completed.",
    invalidItemMessage: "This signal has no timesheet to analyse.",
  });

  useEffect(() => {
    loadQueue(JSON.parse(listFiltersKey));
  }, [listFiltersKey, loadQueue]);

  useEffect(() => {
    loadOverview(JSON.parse(overviewFiltersKey));
  }, [overviewFiltersKey, loadOverview]);

  const chosenCategories = selectedCategories(filters.category);
  const matchedCategory = CATEGORY_OPTIONS.find(
    (option) => option.value === chosenCategories[0]
  );
  const categoryLabel =
    chosenCategories.length === 0
      ? null
      : chosenCategories.length === 1
        ? (matchedCategory && matchedCategory.label) || "this category"
        : `${chosenCategories.length} categories`;
  const categoryCaption =
    matchCaption && categoryLabel
      ? `${total} signal${total === 1 ? "" : "s"} matches ${categoryLabel} + Needs follow-up`
      : null;

  const selectedId = selected ? selected.id : null;
  const [tabCounts, setTabCounts] = useState({});
  const [contentBusy, setContentBusy] = useState(true);
  const [tableBusy, setTableBusy] = useState(false);
  const wasListLoadingRef = useRef(listLoading);
  const lastTableQueryKeyRef = useRef(tableQueryKey);
  const lastPageQueryKeyRef = useRef(
    isLiveView ? null : `${page}:${pageSize}`
  );
  const loadTabCountsRef = useRef(null);

  const signalFromDate = signalDates?.fromDate ?? filters.fromDate;
  const signalToDate = signalDates?.toDate ?? filters.toDate;

  const tabCountFiltersKey = JSON.stringify({
    fromDate: signalFromDate,
    toDate: signalToDate,
    search: (filters.search || "").trim(),
    status: filters.status,
    view: filters.view,
  });

  const loadTabCounts = useCallback(() => {
    const signalRange = {
      fromDate: signalFromDate,
      toDate: signalToDate,
      search: filters.search,
      status: filters.status,
    };
    const currentView = filters.view;
    const fetches = [];

    // Skip the open tab: loadOverview already requested that count for the KPI strip.
    if (currentView !== CARE_WATCH_VIEWS.INCIDENT) {
      fetches.push(
        careWatchApi
          .getCareWatchOverview(
            toApiFilters(
              {
                ...signalRange,
                view: CARE_WATCH_VIEWS.INCIDENT,
                risk: "all",
                category: "all",
              },
              { omit: ["risk", "issue"] }
            )
          )
          .then((res) => [CARE_WATCH_VIEWS.INCIDENT, Number(res?.data?.total ?? 0)])
      );
    }
    if (currentView !== CARE_WATCH_VIEWS.DOCUMENTATION) {
      fetches.push(
        careWatchApi
          .getCareWatchOverview(
            toApiFilters(
              {
                ...signalRange,
                view: CARE_WATCH_VIEWS.DOCUMENTATION,
                issue: "all",
              },
              { omit: ["risk", "issue"] }
            )
          )
          .then((res) => [
            CARE_WATCH_VIEWS.DOCUMENTATION,
            Number(res?.data?.total ?? 0),
          ])
      );
    }
    if (currentView !== CARE_WATCH_VIEWS.LIVE) {
      fetches.push(
        careWatchApi
          .getCareWatchLiveOverview(
            toApiFilters(
              {
                ...signalRange,
                view: CARE_WATCH_VIEWS.LIVE,
                liveState: "all",
              },
              { omit: ["live_state"] }
            )
          )
          .then((res) => [CARE_WATCH_VIEWS.LIVE, Number(res?.data?.on_floor ?? 0)])
      );
    }

    if (fetches.length === 0) return Promise.resolve();

    return Promise.all(fetches)
      .then((pairs) => {
        setTabCounts((prev) => {
          const next = { ...prev };
          pairs.forEach(([key, count]) => {
            next[key] = count;
          });
          return next;
        });
      })
      .catch(() => {
        // Keep the last known tab badges if a count request fails.
      });
  }, [
    signalFromDate,
    signalToDate,
    filters.search,
    filters.status,
    filters.view,
  ]);

  loadTabCountsRef.current = loadTabCounts;

  // Debounce Pusher refreshes — several timesheets can finish analysis
  // together, firing one event each. A 400ms settle window (matching mobile)
  // collapses the burst into a single API round-trip.
  const pusherDebounceRef = useRef(null);
  useEffect(() => {
    if (careWatchUpdatedPusher) {
      clearTimeout(pusherDebounceRef.current);
      pusherDebounceRef.current = setTimeout(() => {
        loadTabCountsRef.current?.();
        if (!isCareWatchLiveView(filters.view)) {
          refreshQueue();
        }
      }, 400);
    }
    resetCareWatchUpdatedPusher();
    return () => clearTimeout(pusherDebounceRef.current);
  }, [careWatchUpdatedPusher]);

  useEffect(() => {
    const count = isLiveView
      ? Number(overview?.on_floor ?? 0)
      : Number(overview?.total ?? 0);
    if (!Number.isFinite(count)) return;
    setTabCounts((prev) =>
      prev[filters.view] === count ? prev : { ...prev, [filters.view]: count }
    );
  }, [filters.view, isLiveView, overview]);

  useEffect(() => {
    loadTabCounts();
  }, [tabCountFiltersKey, loadTabCounts]);

  useEffect(() => {
    const wasLoading = wasListLoadingRef.current;
    wasListLoadingRef.current = listLoading;
    if (wasLoading && !listLoading) {
      setContentBusy(false);
      setTableBusy(false);
    }
  }, [listLoading]);

  // Date, category, risk, status, search, KPI buckets.
  useEffect(() => {
    if (lastTableQueryKeyRef.current === tableQueryKey) return;
    lastTableQueryKeyRef.current = tableQueryKey;
    setTableBusy(true);
  }, [tableQueryKey]);

  // Page and page size ask the server for a new slice. Show the table
  // skeleton until that slice arrives. Live is already in memory.
  useEffect(() => {
    if (isLiveView) return;
    const pageQueryKey = `${page}:${pageSize}`;
    if (lastPageQueryKeyRef.current === pageQueryKey) return;
    lastPageQueryKeyRef.current = pageQueryKey;
    setTableBusy(true);
  }, [page, pageSize, isLiveView]);

    // Incident / missed-docs load the full signal. Live has no detail panel.
    useEffect(() => {
    if (isLiveView) return;
    if (!selectedId || !selected || !selected.timesheetUuid) return;
      loadDetail({
        uuid: selected.timesheetUuid,
        incidentKey: selected.incidentKey,
        view: filters.view,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Overlay sheet has to stay mounted through the close animation; unmounting
  // on selected=null (or using display:none) would skip the slide-out.
  const [sheetItem, setSheetItem] = useState(selected);
  const [sheetOpen, setSheetOpen] = useState(Boolean(selected));
  const [overlayMode, setOverlayMode] = useState(isOverlayViewport);

  useEffect(() => {
    const mq = window.matchMedia(OVERLAY_MAX_WIDTH_MQ);
    const onChange = (event) => setOverlayMode(event.matches);
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
    } else {
      mq.addListener(onChange);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener("change", onChange);
      } else {
        mq.removeListener(onChange);
      }
    };
  }, []);

  useEffect(() => {
    if (selectedId && selected) {
      setSheetItem(selected);
      const frame = requestAnimationFrame(() => setSheetOpen(true));
      return () => cancelAnimationFrame(frame);
    }

    setSheetOpen(false);
    const skipWait = !overlayMode;
    if (skipWait) {
      setSheetItem(null);
      return undefined;
    }
    const timer = window.setTimeout(() => setSheetItem(null), SHEET_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [selectedId, selected, overlayMode]);

  const sheetLocksPage = Boolean(sheetItem) && overlayMode;
  useBodyScrollLock(sheetLocksPage, {
    nestedScrollAllowSelector: "[data-scroll-lock-allow]",
  });

  const detailSlotClass = classNames(
    "care-watch-detail-slot",
    sheetOpen ? "is-open" : "is-closed"
  );
  const shellClass = classNames(
    "care-watch-shell",
    sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300"
  );

  const closeDetail = () => setSelectedId(null);

  useEffect(() => {
    if (isLiveView && selectedId) setSelectedId(null);
  }, [isLiveView, selectedId, setSelectedId]);
  const handleAcknowledge = (item) => acknowledgeIds([item.id]);
  const handlePromote = (item) => promoteItem(item.id);
  const handleKpiClick = useCallback(
    (key) => {
      const updates = kpiClickFilterUpdate(key, filters);
      updates.forEach(({ key: k, value: v }) => updateFilter(k, v));
    },
    [filters, updateFilter]
  );

  const showSkeletons = contentBusy;
  const showFilterSkeletons = contentBusy;
  const showTableSkeleton = contentBusy || tableBusy;

  const handleTabChange = (view) => {
    if (view === filters.view) return;
    if (isCareWatchLiveView(view)) setSelectedId(null);
    setContentBusy(true);
    updateFilter("view", view);
  };

  return (
    <div
      className={classNames(
        "care-watch",
        sideBarPadding && "care-watch--nav-slim",
        sheetLocksPage && "care-watch--sheet-open"
      )}
    >
      <SideBar />
      <div className={shellClass}>
        <div className="care-watch-sticky-bar">
          <CareWatchNoticeBanner />
          <Header
            cssClass="care-watch-header"
            title={<CareWatchPageTitle />}
            desc={<CareWatchPageSubtitle view={filters.view} />}
          />
          <div className="care-watch-tabs-wrap">
            <CareWatchQueueTabs
              value={filters.view}
              counts={tabCounts}
              onChange={handleTabChange}
            />
          </div>
        </div>
        <section className="care-watch-body relative">
          <div className="care-watch-stage" aria-busy={showTableSkeleton}>
            <div className="care-watch-kpis-wrap">
              {showSkeletons ? (
                <CareWatchKpiSkeleton view={filters.view} />
              ) : (
                <CareWatchKpiStrip
                  kpis={kpis}
                  filters={filters}
                  onSelect={handleKpiClick}
                />
              )}
            </div>
            <div className="care-watch-filters-wrap">
              {showFilterSkeletons ? (
                <CareWatchFilterSkeleton
                  live={isLiveView}
                  documentation={
                    filters.view === CARE_WATCH_VIEWS.DOCUMENTATION
                  }
                />
              ) : (
                <CareWatchFilterBar
                  filters={filters}
                  onChange={updateFilter}
                  onChangeMany={updateFilters}
                  onClear={clearFilters}
                  hasActiveFilters={hasActiveFilters}
                />
              )}
            </div>
            <CareWatchWorkArea
              view={filters.view}
              loading={showSkeletons}
              tableLoading={showTableSkeleton}
              detailLoading={detailLoading}
              pageItems={pageItems}
              selectedId={selectedId}
              selected={sheetItem}
              detailSlotClass={detailSlotClass}
              promoteHighlight={promoteHighlight}
              promoteLoading={promoteLoading}
              acknowledgeLoading={acknowledgeLoading}
              page={page}
              totalPages={totalPages}
              total={total}
              pageSize={pageSize}
              categoryCaption={categoryCaption}
              toggleRow={toggleRow}
              toggleChecked={toggleChecked}
              setPage={setPage}
              setPageSize={setPageSize}
              onAcknowledge={handleAcknowledge}
              onPromote={handlePromote}
              onReanalyse={startReanalyse}
              reanalyseLoading={reanalyseStreaming}
              onCloseDetail={closeDetail}
              riskSavingId={riskSavingId}
              onChangeRisk={
                filters.view === CARE_WATCH_VIEWS.INCIDENT
                  ? (row, risk) =>
                      changeRisk({
                        id: row.id,
                        timesheetId: row.timesheetId,
                        incidentKey: row.incidentKey || "",
                        risk,
                        overviewFilters: overviewFiltersRef.current,
                        listFilters: listFiltersRef.current,
                      })
                  : null
              }
            />
          </div>
          {sheetItem && !isLiveView ? (
            <button
              type="button"
              aria-label="Close detail"
              className={classNames(
                "care-watch-detail-backdrop",
                sheetOpen && "is-open"
              )}
              onClick={closeDetail}
            />
          ) : null}
        </section>
      </div>
      <TimesheetAiConfirmModal {...confirmModalProps} />
      <TimesheetAiProgressModal {...progressModalProps} />
    </div>
  );
};

export default CareWatchPage;
